import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { optional } from '@nullvoxpopuli/ember-composable-helpers';
import can from 'ember-can/helpers/can';
import type { Task, TaskGroup } from 'ember-concurrency';
import { task, dropTaskGroup } from 'ember-concurrency';
import FeatureOrUpgradeButton from 'garaje/components/feature-or-upgrade-button';
import SlideToggle from 'garaje/components/slide-toggle';
import MessageBox from 'garaje/components/ui/classic/message-box';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import PageSection from 'garaje/components/ui/page/section';
import ConnectHostNotifications from 'garaje/components/visitors/settings/notifications/connect-host-notifications';
import ConnectNotifications from 'garaje/components/visitors/settings/notifications/connect-notifications';
import CustomText from 'garaje/components/visitors/settings/notifications/custom-text';
import FallbackNotifications, {
  AdditionalNotificationEmails,
} from 'garaje/components/visitors/settings/notifications/fallback-notifications';
import IntegrationsToggle from 'garaje/components/visitors/settings/notifications/integrations-toggle';
import featureEnabled from 'garaje/helpers/feature-enabled';
import type Config from 'garaje/models/config';
import type ConnectLocationConfiguration from 'garaje/models/connect-location-configuration';
import type FallbackContact from 'garaje/models/fallback-contact';
import type Location from 'garaje/models/location';
import type Subscription from 'garaje/models/subscription';
import type User from 'garaje/models/user';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type ImpressionsService from 'garaje/services/impressions';
import type StateService from 'garaje/services/state';
import type StatsigService from 'garaje/services/statsig';
import type PluginPreference from 'garaje/types/plugin-preference';
import { SLACK_V2_PLUGIN_KEY, MSTEAMS_V2_PLUGIN_KEY, IMPRESSION_NAMES } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import LiquidIf from 'liquid-fire/components/liquid-if';
import { or, notEmpty } from 'macro-decorators';

interface HostOptionsComponentSignature {
  Args: {
    closeFeature: () => void;
    config: Config;
    connectLocationConfiguration: ConnectLocationConfiguration;
    fallbackContacts: FallbackContact[];
    feature: string;
    location: Location;
    locationIsConnectedToProperty: boolean;
    onUpdateFallbackContacts: (changes: { hasChanges: boolean }) => void;
    openFeature: () => void;
    pluginPreferences: PluginPreference[];
    reloadFallbackContacts: () => Promise<void>;
    searchUsersTask: Task<User[], [term: string, contacts: User[]]>;
    vrSubscription: Subscription;
  };
}

export default class HostOptionsComponent extends Component<HostOptionsComponentSignature> {
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare impressions: ImpressionsService;
  @service declare state: StateService;
  @service declare statsig: StatsigService;

  @notEmpty('slackPlugins') hasSlackEnabled!: boolean;
  @notEmpty('msTeamsPlugins') hasMSTeamsEnabled!: boolean;
  @or('hasSlackEnabled', 'hasMSTeamsEnabled') hasHostRepliesCompatiblePlugin!: boolean;

  @dropTaskGroup toggleTasks!: TaskGroup<unknown>;

  get msTeamsHostRepliesEnabled(): boolean {
    return this.hasMSTeamsEnabled && this.featureFlags.isEnabled('ms-teams-host-replies');
  }

  get showHostRepliesSection(): boolean {
    return this.hasHostRepliesCompatiblePlugin && this.featureFlags.isEnabled('two-way-host-messaging');
  }

  get slackPlugins(): PluginPreference[] {
    return this.args.pluginPreferences?.filter(
      (pluginPreference) => pluginPreference.plugin.key === SLACK_V2_PLUGIN_KEY,
    );
  }

  get msTeamsPlugins(): PluginPreference[] {
    return this.args.pluginPreferences?.filter(
      (pluginPreference) => pluginPreference.plugin.key === MSTEAMS_V2_PLUGIN_KEY,
    );
  }

  get canShowAddEmployeesTooltip(): boolean {
    if (!this.args.location?.hostNotificationsEnabled) return false;
    if (this.state?._companyMeta?.['employees-count'] !== 1) return false;
    if (!this.featureFlags.isEnabled('growth_employee_directory_empty_state')) return false;

    return true;
  }

  saveLocation = task({ drop: true }, async (location: Location) => {
    try {
      await location.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      location.rollbackAttributes();
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  toggleEmailNotificationEnabled = task({ group: 'toggleTasks' }, async (value: boolean) => {
    this.args.location.emailNotificationEnabled = value;
    await this.saveLocation.perform(this.args.location);
    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.HOST_OPTIONS_EMAIL_NOTIFICATION[value ? 'ENABLED' : 'DISABLED'],
    );
  });

  toggleSMSNotificationEnabled = task({ group: 'toggleTasks' }, async (value: boolean) => {
    this.args.location.smsNotificationEnabled = value;
    await this.saveLocation.perform(this.args.location);
    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.HOST_OPTIONS_SMS_NOTIFICATION[value ? 'ENABLED' : 'DISABLED'],
    );
  });

  toggleHostNotificationsEnabled = task({ group: 'toggleTasks' }, async (value: boolean) => {
    this.args.location.hostNotificationsEnabled = value;
    await this.saveLocation.perform(this.args.location);

    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.HOST_OPTIONS_HOST_NOTIFICATIONS[value ? 'ENABLED' : 'DISABLED'],
    );
  });

  toggleNotifyReceptionistsOnHostReplyEnabled = task({ group: 'toggleTasks' }, async (value: boolean) => {
    this.args.location.notifyReceptionistsOnHostReplyEnabled = value;
    await this.saveLocation.perform(this.args.location);
    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.HOST_OPTIONS_RECEPTIONIST_NOTIFICATION[value ? 'ENABLED' : 'DISABLED'],
    );
  });

  @action
  trackTooltipShown(): void {
    this.statsig.logEvent('employee-host-tooltip-seen');
  }

  @action
  trackTooltipLinkClick(): void {
    this.statsig.logEvent('employee-host-tooltip-directory-clicked');
  }

  @action
  handleEmailNotificationToggle(event: Event): void {
    const target = event.target as HTMLInputElement;
    void this.toggleEmailNotificationEnabled.perform(target.checked);
  }

  @action
  handleSMSNotificationToggle(event: Event): void {
    const target = event.target as HTMLInputElement;
    void this.toggleSMSNotificationEnabled.perform(target.checked);
  }

  <template>
    <PageSection>
      <h3>Host notifications</h3>

      {{#if @locationIsConnectedToProperty}}
        <ConnectHostNotifications
          @location={{@location}}
          @connectLocationConfiguration={{@connectLocationConfiguration}}
        />
      {{else}}
        <SettingsPanel data-test-host-notifications @isExpandable={{false}} as |panel|>
          <panel.header
            @iconUri="/assets/images/features/notifications.svg"
            @title="Host notifications"
            @description="Automatically notify employees when their visitors arrive."
            as |header|
          >
            <header.controls>
              <SlideToggle
                data-test-toggle-host-notifications
                @isDisabled={{this.toggleTasks.isRunning}}
                @isChecked={{@location.hostNotificationsEnabled}}
                @onClick={{this.toggleHostNotificationsEnabled.perform}}
              />
            </header.controls>
          </panel.header>
          {{#if this.canShowAddEmployeesTooltip}}
            <MessageBox
              @type="attention"
              @header="Add employees to maximize visitor invites"
              class="my-2 ml-16 w-[600px]"
              {{didInsert this.trackTooltipShown}}
              {{on "click" this.trackTooltipLinkClick}}
            >
              <p class="mb-0">
                Add more employees to the
                <LinkTo data-test-link-to-email-templates @route="employees.directory">
                  employee directory
                </LinkTo>
                so they can be selected as host.
              </p>
            </MessageBox>
          {{/if}}
        </SettingsPanel>
      {{/if}}

      {{#LiquidIf @location.hostNotificationsEnabled}}
        <CustomText
          @model={{@location}}
          @feature={{@feature}}
          @closeFeature={{optional @closeFeature}}
          @openFeature={{optional @openFeature}}
        />
      {{/LiquidIf}}

      <FallbackNotifications
        @fallbackContacts={{@fallbackContacts}}
        @onUpdateFallbackContacts={{@onUpdateFallbackContacts}}
        @reloadFallbackContacts={{@reloadFallbackContacts}}
      />

      {{#if (featureEnabled "additional-host-notification-recipients")}}
        <AdditionalNotificationEmails @config={{@config}} @onUpdate={{@onUpdateFallbackContacts}} />
      {{/if}}

      <p class="mb-0 text-sm">Learn more about
        <a
          href="https://envoy.help/en/articles/3444381-host-notifications"
          target="_blank"
          rel="noopener"
          tabindex="-1"
        >host notifications</a>.</p>
    </PageSection>

    {{#if @locationIsConnectedToProperty}}
      <ConnectNotifications
        @location={{@location}}
        @searchUsersTask={{@searchUsersTask}}
        @connectLocationConfiguration={{@connectLocationConfiguration}}
      />
    {{/if}}

    {{#LiquidIf @location.hostNotificationsEnabled}}
      <PageSection>
        <div>
          <h3>Default host notification methods</h3>
          <p class="mt-2 mb-0">Employees will be notified with these default methods, unless they customize their own
            notification settings.</p>
        </div>
        <SettingsPanel as |panel|>
          <panel.header @iconUri="/assets/images/features/apns.svg" @title="Envoy Mobile app" as |header|>
            <header.description>
              Employees can install
              <a
                href="https://apps.apple.com/us/app/envoy-your-workplace-app/id983102576"
                target="_blank"
                rel="noopener"
                tabindex="-1"
              >Envoy Mobile for iOS</a>
              or
              <a
                href="https://play.google.com/store/apps/details?id=com.envoy.app"
                target="_blank"
                rel="noopener"
                tabindex="-1"
              >Android</a>
              to receive a push notification when their visitor arrives.
            </header.description>
          </panel.header>
        </SettingsPanel>

        {{#unless (featureEnabled "host-notification-plugins-enabled")}}
          <SettingsPanel as |panel|>
            <panel.header
              @iconUri="/assets/images/features/email.svg"
              @title="Email"
              @description="Send employees an email when their visitor arrives."
              as |header|
            >
              <header.controls>
                <label class="toggle">
                  <input
                    checked={{@location.emailNotificationEnabled}}
                    data-test-email-notification
                    disabled={{this.toggleTasks.isRunning}}
                    {{on "change" this.handleEmailNotificationToggle}}
                    type="checkbox"
                  />
                  <span></span>
                </label>
              </header.controls>
            </panel.header>
          </SettingsPanel>

          <SettingsPanel data-test-sms as |panel|>
            <panel.header
              @iconUri="/assets/images/features/sms.svg"
              @title="SMS"
              @description="Send employees an SMS when their visitor arrives."
              as |header|
            >
              <header.controls>
                <FeatureOrUpgradeButton
                  @level="premium"
                  @upgradeQueryParams={{hash plan="premium" period="monthly"}}
                  @isAuthorized={{@vrSubscription.canAccessSmsNotification}}
                  @featureName="sms_notification"
                  @isSlack={{false}}
                  @logCTAEvents={{true}}
                  @shouldCheckMinLevel={{false}}
                  @vrOnly={{false}}
                >
                  <label class="toggle">
                    <input
                      checked={{@location.smsNotificationEnabled}}
                      data-test-sms-notification
                      disabled={{this.toggleTasks.isRunning}}
                      {{on "change" this.handleSMSNotificationToggle}}
                      type="checkbox"
                    />
                    <span></span>
                  </label>
                </FeatureOrUpgradeButton>
              </header.controls>
            </panel.header>
          </SettingsPanel>
        {{/unless}}

        <IntegrationsToggle @title="" @location={{@location}} @pluginPreferences={{@pluginPreferences}} />

        {{#if (can "visit advance-settings")}}
          <p class="text-sm mb-0">
            Don't see this location's preferred notification method?
            <LinkTo @route="integrations">Browse integrations →</LinkTo>
          </p>
        {{/if}}
      </PageSection>
    {{/LiquidIf}}

    {{#LiquidIf this.showHostRepliesSection}}
      <PageSection>
        <h3>Host replies</h3>

        <SettingsPanel @isExpandable={{false}} as |panel|>
          <panel.header
            @iconUri="/assets/images/monitor-security-desk.svg"
            @title="Host replies"
            @description="Send the host's reply to the front desk admin."
            as |header|
          >
            <header.controls>
              <SlideToggle
                data-test-host-replies-toggle
                @isChecked={{@location.notifyReceptionistsOnHostReplyEnabled}}
                @onClick={{this.toggleNotifyReceptionistsOnHostReplyEnabled.perform}}
              />
            </header.controls>
          </panel.header>
        </SettingsPanel>
      </PageSection>
    {{/LiquidIf}}
  </template>
}
